import { createFileRoute, useParams } from '@tanstack/react-router';
import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Heart, MessageSquare } from 'lucide-react'; // Icons
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { formatDistanceToNow } from 'date-fns'; // For relative time
import { fetchApi } from '@/lib/api'; // <-- Import fetchApi

// --- TypeScript Types ---

// From backend models/models.go
interface BaseEntry {
  id: number;
  event_id: number; // Not directly used in frontend display usually
  guest_name: string;
  guest_email: string; // Needed for actions, but maybe not displayed
  media_type: 'photo' | 'text';
  content: string; // Text content or *relative path within event dir* for photo
  caption: string | null;
  is_private: boolean; // Should always be false for public entries endpoint
  uploaded_at: string; // ISO 8601 date string
}

interface Comment {
  id: number;
  entry_id: number;
  guest_name: string;
  guest_email: string;
  comment_text: string;
  commented_at: string; // ISO 8601 date string
}

// From backend handlers/event_handlers.go PublicEntryResponse
interface PublicEntry extends BaseEntry {
  like_count: number;
  comments: Comment[]; // Recent comments
  relative_path?: string; // Path relative to the static server base URL for photos
}

// From backend handlers/event_handlers.go PublicApiEvent (for event name)
interface PublicApiEvent {
  name: string;
}

// Type for comment input state
type CommentInputs = {
  [entryId: number]: string;
};

export const Route = createFileRoute('/event/$eventLinkId')({
  component: PublicEventPage,
});

function PublicEventPage() {
  const { eventLinkId } = useParams({ from: '/event/$eventLinkId' });

  // --- State ---
  const [event, setEvent] = useState<PublicApiEvent | null>(null);
  const [publicEntries, setPublicEntries] = useState<PublicEntry[]>([]);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [error, setError] = useState<string | null>(null); // Combined error state for initial load

  // Guest Info State (from RFC-006)
  const [guestName, setGuestName] = useState<string>('');
  const [guestEmail, setGuestEmail] = useState<string>('');
  const [hasGuestInfo, setHasGuestInfo] = useState<boolean>(false);
  const [isGuestInfoDialogOpen, setIsGuestInfoDialogOpen] =
    useState<boolean>(false);
  const guestNameInputRef = useRef<HTMLInputElement>(null); // For focusing
  const [guestInfoError, setGuestInfoError] = useState<string | null>(null); // Specific error for dialog

  // Entry Form State (from RFC-006)
  const [mediaType, setMediaType] = useState<'text' | 'photo'>('text');
  const [content, setContent] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState<string>('');
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [isSubmittingEntry, setIsSubmittingEntry] = useState<boolean>(false);
  const [entrySubmitStatus, setEntrySubmitStatus] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);

  // Like/Comment State (New for RFC-007)
  const [likedEntryIds, setLikedEntryIds] = useState<Set<number>>(new Set()); // Track liked entries by ID
  const [commentInputs, setCommentInputs] = useState<CommentInputs>({}); // Track comment input per entry
  const [isLiking, setIsLiking] = useState<{ [entryId: number]: boolean }>({}); // Loading state per like button
  const [isCommenting, setIsCommenting] = useState<{
    [entryId: number]: boolean;
  }>({}); // Loading state per comment form
  const [actionError, setActionError] = useState<{
    [entryId: number]: string | null;
  }>({}); // Error per entry action section
  const [galleryError, setGalleryError] = useState<string | null>(null); // Specific error for gallery loading

  // --- Effects ---

  // Effect to load guest info from session storage
  useEffect(() => {
    const storedName = sessionStorage.getItem(`guestName_${eventLinkId}`);
    const storedEmail = sessionStorage.getItem(`guestEmail_${eventLinkId}`);
    if (storedName && storedEmail) {
      setGuestName(storedName);
      setGuestEmail(storedEmail);
      setHasGuestInfo(true);
    }
  }, [eventLinkId]);

  // Effect to fetch Event Name and Public Entries
  useEffect(() => {
    if (!eventLinkId) return;

    const fetchEventData = async () => {
      setLoadingEvent(true);
      setLoadingEntries(true);
      setError(null); // Reset combined error

      try {
        const [eventResponse, entriesResponse] = await Promise.all([
          fetchApi(`/api/events/${eventLinkId}`), // <-- Use fetchApi
          fetchApi(`/api/events/${eventLinkId}/entries/public`), // <-- Use fetchApi
        ]);

        // Handle Event Name Response
        if (eventResponse.status === 404) throw new Error('Event not found.');
        if (!eventResponse.ok)
          throw new Error(
            `Failed to load event details (Status: ${eventResponse.status})`,
          );
        const eventData: PublicApiEvent = await eventResponse.json();
        setEvent(eventData);
        setLoadingEvent(false);

        // Handle Public Entries Response
        if (!entriesResponse.ok) {
          // Don't throw 404 again if event was found but entries failed
          if (entriesResponse.status !== 404) {
            // If event exists but entries fail for other reason
            console.warn(
              `Failed to load public entries (Status: ${entriesResponse.status})`,
            );
            // Set empty array or handle differently? For now, show empty gallery.
          }
          // If entries 404 (e.g., no public entries yet), it's not necessarily an error for the whole page
          setPublicEntries([]);
        } else {
          const entriesData: PublicEntry[] = await entriesResponse.json();
          setPublicEntries(entriesData);
        }
        setLoadingEntries(false);
      } catch (err) {
        console.error('Failed to fetch event data:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';
        setError(errorMessage);
        setEvent(null); // Reset event state on error
        setPublicEntries([]); // Reset entries state on error
        setLoadingEvent(false);
        setLoadingEntries(false);
      }
    };

    fetchEventData();
  }, [eventLinkId]); // Re-fetch if eventLinkId changes

  // Effect to control guest info dialog opening
  useEffect(() => {
    // Open dialog only if event/entry loading is complete AND guest info is missing
    if (!loadingEvent && !loadingEntries && !hasGuestInfo) {
      setIsGuestInfoDialogOpen(true);
    }
  }, [loadingEvent, loadingEntries, hasGuestInfo]);

  // --- Event Handlers ---

  const handleGuestInfoSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGuestInfoError(null); // Clear previous errors
    const name = guestNameInputRef.current?.value.trim();
    const email = guestEmail.trim();

    if (name && email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setGuestName(name);
      setGuestEmail(email);
      sessionStorage.setItem(`guestName_${eventLinkId}`, name);
      sessionStorage.setItem(`guestEmail_${eventLinkId}`, email);
      setHasGuestInfo(true);
      setIsGuestInfoDialogOpen(false);
    } else {
      setGuestInfoError('Please enter a valid name and email address.');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setContent(''); // Clear text content if switching to photo
    }
  };

  const handleEntrySubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasGuestInfo || !eventLinkId || isSubmittingEntry) return;

    setIsSubmittingEntry(true);
    setEntrySubmitStatus(null);

    const formData = new FormData();
    formData.append('guest_name', guestName);
    formData.append('guest_email', guestEmail);
    formData.append('media_type', mediaType);
    formData.append('caption', caption);
    formData.append('is_private', String(isPrivate));

    if (mediaType === 'photo' && file) {
      formData.append('file', file);
    } else if (mediaType === 'text' && content.trim()) {
      formData.append('content', content.trim());
    } else {
      setEntrySubmitStatus({
        ok: false,
        message: 'Please provide content (text or photo).',
      });
      setIsSubmittingEntry(false);
      return;
    }

    try {
      const response = await fetchApi(`/api/events/${eventLinkId}/entries`, { // <-- Use fetchApi
        method: 'POST',
        body: formData,
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.message || `HTTP error! status: ${response.status}`,
        );
      }

      setEntrySubmitStatus({
        ok: true,
        message: 'Entry submitted successfully!',
      });
      // Reset form
      setMediaType('text');
      setContent('');
      setFile(null);
      setCaption('');
      setIsPrivate(false);

      // Refresh gallery by adding the new entry optimistically OR refetching
      // Optimistic update (simple version: refetch all)
      await refetchPublicEntries();
    } catch (err) {
      console.error('Failed to submit entry:', err);
      setEntrySubmitStatus({
        ok: false,
        message:
          err instanceof Error ? err.message : 'An unknown error occurred.',
      });
    } finally {
      setIsSubmittingEntry(false);
      setTimeout(() => setEntrySubmitStatus(null), 5000);
    }
  };

  // Handler for Like/Unlike button
  const handleLikeToggle = async (entryId: number) => {
    if (!hasGuestInfo || isLiking[entryId]) return;

    setIsLiking((prev) => ({ ...prev, [entryId]: true }));
    setActionError((prev) => ({ ...prev, [entryId]: null }));

    const isCurrentlyLiked = likedEntryIds.has(entryId);
    const method = isCurrentlyLiked ? 'DELETE' : 'POST';
    const url = `/api/entries/${entryId}/like`;

    // Optimistic UI update
    setLikedEntryIds((prev) => {
      const newSet = new Set(prev);
      if (isCurrentlyLiked) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
    setPublicEntries((prevEntries) =>
      prevEntries.map((entry) =>
        entry.id === entryId
          ? {
              ...entry,
              like_count: entry.like_count + (isCurrentlyLiked ? -1 : 1),
            }
          : entry,
      ),
    );

    try {
      const response = await fetchApi(url, { // <-- Use fetchApi
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_email: guestEmail }),
      });

      if (!response.ok && response.status !== 304) {
        // 304 Not Modified is ok for re-like/unlike
        // Revert optimistic update on failure
        setLikedEntryIds((prev) => {
          const newSet = new Set(prev);
          if (isCurrentlyLiked) {
            newSet.add(entryId); // Add it back if DELETE failed
          } else {
            newSet.delete(entryId); // Remove it if POST failed
          }
          return newSet;
        });
        setPublicEntries((prevEntries) =>
          prevEntries.map((entry) =>
            entry.id === entryId
              ? {
                  ...entry,
                  like_count: entry.like_count + (isCurrentlyLiked ? 1 : -1),
                } // Revert count
              : entry,
          ),
        );

        const errorData = await response.json().catch(() => ({})); // Try to get error message
        throw new Error(
          errorData.message ||
            `Failed to ${isCurrentlyLiked ? 'unlike' : 'like'} (Status: ${response.status})`,
        );
      }

      // Optional: Refetch entry details to ensure consistency if optimistic update is complex
      // await refetchPublicEntries(); // Or just rely on optimistic update
    } catch (err) {
      console.error(
        `Failed to ${isCurrentlyLiked ? 'unlike' : 'like'} entry:`,
        err,
      );
      setActionError((prev) => ({
        ...prev,
        [entryId]:
          err instanceof Error ? err.message : 'Could not perform action.',
      }));
    } finally {
      setIsLiking((prev) => ({ ...prev, [entryId]: false }));
      setTimeout(
        () => setActionError((prev) => ({ ...prev, [entryId]: null })),
        5000,
      );
    }
  };

  // Handler for Comment Input Change
  const handleCommentInputChange = (entryId: number, value: string) => {
    setCommentInputs((prev) => ({
      ...prev,
      [entryId]: value,
    }));
  };

  // Handler for Comment Submission
  const handleCommentSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    entryId: number,
  ) => {
    event.preventDefault();
    if (
      !hasGuestInfo ||
      !commentInputs[entryId]?.trim() ||
      isCommenting[entryId]
    )
      return;

    setIsCommenting((prev) => ({ ...prev, [entryId]: true }));
    setActionError((prev) => ({ ...prev, [entryId]: null }));

    const commentText = commentInputs[entryId].trim();

    try {
      const response = await fetchApi(`/api/entries/${entryId}/comments`, { // <-- Use fetchApi
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_name: guestName,
          guest_email: guestEmail,
          comment_text: commentText,
        }),
      });

      const newComment: Comment = await response.json(); // Assumes backend returns the created comment

      if (!response.ok) {
        throw new Error(
          newComment.comment_text ||
            `Failed to add comment (Status: ${response.status})`,
        );
      }

      // Optimistic UI Update: Add comment to the list
      setPublicEntries((prevEntries) =>
        prevEntries.map((entry) =>
          entry.id === entryId
            ? { ...entry, comments: [...entry.comments, newComment] } // Add new comment
            : entry,
        ),
      );

      // Clear input field for this entry
      setCommentInputs((prev) => ({ ...prev, [entryId]: '' }));
    } catch (err) {
      console.error('Failed to submit comment:', err);
      setActionError((prev) => ({
        ...prev,
        [entryId]:
          err instanceof Error ? err.message : 'Could not submit comment.',
      }));
    } finally {
      setIsCommenting((prev) => ({ ...prev, [entryId]: false }));
      // Don't auto-clear error here, let user see it near the input
      // setTimeout(() => setActionError(prev => ({ ...prev, [entryId]: null })), 5000);
    }
  };

  // Helper to refetch entries (e.g., after posting)
  const refetchPublicEntries = async () => {
    if (!eventLinkId) return;
    setLoadingEntries(true); // Show loading state for gallery
    setGalleryError(null); // Clear previous gallery errors
    try {
      const response = await fetchApi(`/api/events/${eventLinkId}/entries/public`); // <-- Use fetchApi
      if (!response.ok) {
        if (response.status !== 404) {
          // 404 is ok (no entries)
          throw new Error(
            `Failed to refetch entries (Status: ${response.status})`,
          );
        }
        setPublicEntries([]); // Handle case where last entry might have been deleted/made private
      } else {
        const entriesData: PublicEntry[] = await response.json();
        setPublicEntries(entriesData);
      }
    } catch (err) {
      console.error('Failed to refetch public entries:', err);
      // Consider setting an error state specific to the gallery?
      setGalleryError(
        err instanceof Error ? err.message : 'Could not refresh gallery.',
      );
    } finally {
      setLoadingEntries(false);
    }
  };

  // --- Render Functions ---

  const renderGuestInfoDialog = () => (
    <Dialog
      open={isGuestInfoDialogOpen}
      onOpenChange={setIsGuestInfoDialogOpen}
    >
      <DialogContent
        className="sm:max-w-[425px]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <form onSubmit={handleGuestInfoSubmit}>
          <DialogHeader>
            <DialogTitle>Welcome!</DialogTitle>
            <DialogDescription>
              Please provide your name and email to participate.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                ref={guestNameInputRef}
                placeholder="Your Name"
                className="col-span-3 text-foreground"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="col-span-3 text-foreground"
                required
              />
            </div>
            {guestInfoError && (
              <p className="col-span-4 text-sm text-red-600 text-center">
                {guestInfoError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="submit">Save details</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderEntryForm = () => (
    <Card className="mt-6 mb-8">
      {' '}
      {/* Added mb-8 */}
      <CardHeader>
        <CardTitle>Share Your Memory</CardTitle>
        <CardDescription>
          Upload a photo or write a message for {event?.name || 'the event'}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleEntrySubmit} className="space-y-4">
          <RadioGroup
            defaultValue="text"
            value={mediaType}
            onValueChange={(value: 'text' | 'photo') => {
              setMediaType(value);
              setFile(null);
              setContent('');
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="text" id="r1" />
              <Label htmlFor="r1">Write a Message</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="photo" id="r2" />
              <Label htmlFor="r2">Upload a Photo</Label>
            </div>
          </RadioGroup>
          {mediaType === 'text' ? (
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Your message..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </div>
          ) : (
            <div>
              <Label htmlFor="picture">Photo</Label>
              <Input
                id="picture"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                required
              />
              <small>(Max 5MB)</small>{' '}
              {file && (
                <p className="text-sm text-muted-foreground mt-1">
                  Selected: {file.name}
                </p>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="caption">Caption (Optional)</Label>
            <Input
              id="caption"
              placeholder="Add a caption..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="private"
              checked={isPrivate}
              onCheckedChange={(checked: CheckedState) =>
                setIsPrivate(checked === true)
              }
            />
            <Label htmlFor="private">
              Make this entry private (only visible to the couple)
            </Label>
          </div>
          {entrySubmitStatus && (
            <p
              className={`text-sm ${entrySubmitStatus.ok ? 'text-green-600' : 'text-red-600'}`}
            >
              {entrySubmitStatus.message}
            </p>
          )}
          <Button type="submit" disabled={isSubmittingEntry}>
            {isSubmittingEntry ? 'Submitting...' : 'Submit Entry'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  // Render the Public Gallery (New for RFC-007)
  const renderGallery = () => {
    if (loadingEntries) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      );
    }

    if (!publicEntries.length) {
      return (
        <p className="text-muted-foreground text-center p-8">
          No public entries yet. Be the first to share!
        </p>
      );
    }

    return (
      <div className="space-y-6">
        {publicEntries.map((entry) => (
          <Card key={entry.id}>
            <CardHeader>
              <CardTitle className="text-lg font-medium">
                {entry.guest_name}
              </CardTitle>
              <CardDescription>
                {formatDistanceToNow(new Date(entry.uploaded_at), {
                  addSuffix: true,
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {entry.media_type === 'photo' ? (
                <img
                  // Construct URL using base path + relative path from entry
                  // Ensure the static path in Go (`e.Static`) matches this base
                  src={`/api/uploads/events/${entry.relative_path}`} // Added /api prefix
                  alt={entry.caption || `Photo by ${entry.guest_name}`}
                  className="rounded-md object-cover w-full max-h-96" // Added max-h
                  loading="lazy" // Lazy load images
                />
              ) : (
                <p className="whitespace-pre-wrap">{entry.content}</p> // Preserve whitespace/newlines
              )}
              {entry.caption && (
                <p className="text-sm text-muted-foreground mt-2">
                  {entry.caption}
                </p>
              )}
            </CardContent>
            <CardFooter className="flex flex-col items-start space-y-4">
              {/* --- Likes --- */}
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleLikeToggle(entry.id)}
                  disabled={!hasGuestInfo || isLiking[entry.id]}
                  aria-label={likedEntryIds.has(entry.id) ? 'Unlike' : 'Like'}
                  className={`flex items-center space-x-1 ${!hasGuestInfo ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <Heart
                    className={`w-4 h-4 ${likedEntryIds.has(entry.id) ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`}
                  />
                  <span>{entry.like_count}</span>
                </Button>
                {!hasGuestInfo && (
                  <span className="text-xs text-muted-foreground">
                    (Login required)
                  </span>
                )}
              </div>

              {/* --- Comments Section --- */}
              <div className="w-full space-y-3">
                {/* Existing Comments */}
                {entry.comments && entry.comments.length > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    {entry.comments
                      .slice(0)
                      .reverse()
                      .map(
                        (
                          comment, // Show newest first
                        ) => (
                          <div key={comment.id} className="text-sm">
                            <span className="font-semibold">
                              {comment.guest_name}:{' '}
                            </span>
                            <span>{comment.comment_text}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              (
                              {formatDistanceToNow(
                                new Date(comment.commented_at),
                                { addSuffix: true },
                              )}
                              )
                            </span>
                          </div>
                        ),
                      )}
                  </div>
                )}

                {/* Comment Form */}
                {hasGuestInfo ? (
                  <form
                    onSubmit={(e) => handleCommentSubmit(e, entry.id)}
                    className="flex items-center space-x-2 pt-2 border-t"
                  >
                    <Input
                      type="text"
                      placeholder="Add a comment..."
                      value={commentInputs[entry.id] || ''}
                      onChange={(e) =>
                        handleCommentInputChange(entry.id, e.target.value)
                      }
                      className="flex-grow"
                      disabled={isCommenting[entry.id]}
                      required
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={
                        isCommenting[entry.id] ||
                        !commentInputs[entry.id]?.trim()
                      }
                    >
                      {isCommenting[entry.id] ? (
                        '...'
                      ) : (
                        <MessageSquare className="w-4 h-4" />
                      )}
                    </Button>
                  </form>
                ) : (
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    (Provide your details above to comment)
                  </p>
                )}
                {/* Action Error specific to this entry */}
                {actionError[entry.id] && (
                  <p className="text-xs text-red-600 mt-1">
                    {actionError[entry.id]}
                  </p>
                )}
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  };

  // --- Main Rendering Logic ---

  // Combined loading state for initial page structure
  const isLoadingInitial =
    loadingEvent || (!hasGuestInfo && isGuestInfoDialogOpen); // Consider dialog part of initial load?

  if (isLoadingInitial && !error) {
    // Show skeleton only if no major error yet
    return (
      <div className="container mx-auto p-4 max-w-3xl">
        {' '}
        {/* Increased max-width */}
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-20 w-full mb-6" />{' '}
            {/* Placeholder for entry form area? */}
            <h2 className="text-2xl font-semibold mb-4">Gallery</h2>
            <Skeleton className="h-40 w-full" /> {/* Placeholder for gallery */}
          </CardContent>
        </Card>
        {/* Render dialog detached if needed during loading */}
        {!hasGuestInfo && renderGuestInfoDialog()}
      </div>
    );
  }

  // Handle major event fetch error AFTER loading attempt
  if (error) {
    return (
      <div className="container mx-auto p-4 max-w-2xl">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
          </CardContent>
        </Card>
        {/* Allow providing guest info even if event/entries failed */}
        {!hasGuestInfo && renderGuestInfoDialog()}
      </div>
    );
  }

  // Main content render (Event loaded, potentially loading entries)
  return (
    <div className="container mx-auto p-4 max-w-3xl">
      {' '}
      {/* Increased max-width */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>
            {event ? event.name : <Skeleton className="h-8 w-3/4" />}
          </CardTitle>
          <CardDescription>
            Welcome{guestName ? `, ${guestName}` : ''}! Share your photos and
            messages below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasGuestInfo ? (
            renderEntryForm() // Show entry form only if guest info provided
          ) : (
            <p className="text-muted-foreground text-center p-8">
              Please provide your details to share an entry.
            </p>
          )}
        </CardContent>
      </Card>
      {/* Gallery Section */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Gallery</h2>
        {galleryError && (
          <p className="text-sm text-red-600 mb-4">{galleryError}</p>
        )}
        {renderGallery()}
      </div>
      {/* Render the guest info dialog (controlled by state) */}
      {renderGuestInfoDialog()}
    </div>
  );
}
